import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";

const easeElegant = [0.76, 0, 0.24, 1];

export const AboutTeam = () => {
  const { t } = useLang();
  const pillars = t("about.pillars") || [];
  const partners = t("about.partners") || [];

  return (
    <section
      className="relative bg-[#0A0A0A] py-28 md:py-40 border-t border-white/10"
      data-testid="about-team"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          {/* Left image, deliberately clipped, portrait aspect */}
          <div className="md:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 1.2, ease: easeElegant }}
              className="relative aspect-[3/4] w-full overflow-hidden border border-[#C5A059]/25"
            >
              <img
                src="https://images.unsplash.com/photo-1780855844203-042553596594?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxyZXN0b3JhdGlvbiUyMGludGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4NDA0MTM1M3ww&ixlib=rb-4.1.0&q=85"
                alt="Weathered frescoes and architectural details"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/80 via-transparent to-transparent pointer-events-none" />
            </motion.div>
            <div className="mt-4 flex items-center gap-3">
              <span className="block h-px w-8 bg-[#C5A059]" />
              <span className="overline !text-[#A1A1AA]">Umbria · patrimonio storico</span>
            </div>
          </div>

          {/* Right content */}
          <div className="md:col-span-7">
            <div className="flex items-center gap-4 mb-8">
              <span className="block h-px w-12 bg-[#C5A059]" />
              <span className="overline">{t("about.overline")}</span>
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 1.1, ease: easeElegant }}
              className="font-serif text-[#F4F4F0] text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight max-w-[22ch]"
            >
              {t("about.title")}
            </motion.h2>
            <p className="mt-8 text-[#A1A1AA] text-base md:text-lg leading-[1.65] font-light max-w-[60ch]">
              {t("about.body")}
            </p>

            {/* Pillars */}
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10 border-t border-white/10 pt-10">
              {pillars.map((p, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-5% 0px" }}
                  transition={{ duration: 0.9, delay: i * 0.06, ease: easeElegant }}
                >
                  <div className="flex items-baseline gap-4 mb-3">
                    <span className="font-mono-ish text-xs text-[#C5A059] tracking-[0.24em]">
                      0{i + 1}
                    </span>
                    <h4 className="font-serif italic text-[#F4F4F0] text-2xl font-light">
                      {p.title}
                    </h4>
                  </div>
                  <p className="text-[#A1A1AA] text-sm leading-[1.65] font-light">
                    {p.body}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Partners */}
            <div className="mt-16 grid grid-cols-1 md:grid-cols-2 border-t border-white/10">
              {partners.map((p, i) => (
                <div
                  key={i}
                  className={`py-8 ${i === 0 ? "md:border-r md:border-white/10 md:pr-8" : "md:pl-8"}`}
                  data-testid={`partner-${i}`}
                >
                  <span className="overline !text-[#52525B]">{p.role}</span>
                  <div className="mt-3 font-serif text-[#F4F4F0] text-2xl md:text-3xl font-light tracking-tight">
                    {p.name}
                  </div>
                  <div className="mt-2 text-[#A1A1AA] text-sm font-light">{p.detail}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
